import React, { Component, Fragment } from 'react';
import ArrowDown from '@material-ui/icons/ArrowDropDownTwoTone';
import Close from '@material-ui/icons/CloseTwoTone';
import Edit from '@material-ui/icons/EditTwoTone';
import {
  MenuItem,
  Theme,
  MenuList,
  ListItemText,
  Divider,
} from '@material-ui/core';
import { WithStyles, withStyles, createStyles } from '@material-ui/core/styles/index';
import ActionBarMenu from './ActionBar/ActionBarMenu';
import ActionBarButton from './ActionBar/ActionBarButton';
import QuantifyTreeButton from './QuantifyTreeButton';
import UserQuantificationSettings from './UserQuantificationSettings';
import { getConfigurations, updateCurrentlySelected, deleteConfiguration } from './Utils';
import {PreferencesJSON} from 'hcla-web-frontend-primitives/app/config/PreferencesProvider';

const styles = (theme: Theme) => createStyles({
  buttonGroup: {
    display: 'flex',
  },
  newConfiguration: {
    textAlign: 'center',
    fontSize: '15px',
    fontWeight: 'bold',
  },
  allConfigs: {
    fontSize: '15px',
    fontWeight: 500,
  },
  downIcon: {
    paddingTop: '0px',
    paddingBottom: '0px',
    marginLeft: theme.spacing(0.5),
    marginRight: '12px',
    width: '2em',
    minWidth: 'unset',
  },
  menu: {
    maxHeight: '30vh',
    overflowY: 'auto',
  },
  editIcon: {
    padding: '0 8px 0 8px',
    marginRight: '1rem',
    width: '2em',
    minWidth: 'unset',
  },
  deleteIcon: {
    padding: '0 8px 0 8px',
    width: '2em',
    minWidth: 'unset',
  },
});

export interface Configurations {
  configuration?: {
    constructor?: {
      replace_transfer_gates_with_basic_events?: boolean
    },
    engine?: {
      BBNSolver?: number;
      BDDConstructor?: number;
      OrderingRule?: number;
    },
    quantify?: {
      type?: string;
      targets?: string;
      mission_test_interval?: number;
      sampling?: {
        method?: string;
        number_of_samples?: number;
        confidence_level?: number;
      };
      importance?: {
        events?: string;
        measures?: string[];
      };
    }
  },
}

interface Props extends WithStyles<typeof styles> {
  quantifyDisabled: boolean;
  modelId: number;
  treeId: number;
  treeType: string;
  classes: any;
  getPreference: (name: string) => Partial<PreferencesJSON>;
  setPreference: (key: string, value: any) => void;
}

interface State {
  addNewConfiguration: boolean;
  configurations: { [key: string]: Configurations };
  anchorEl: EventTarget & HTMLElement;
  currentConfig: string;
  editableConfig: { [key: string]: Configurations };
  currentKey: string;
  newConfig: boolean;
}

class Quantification extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      addNewConfiguration: false,
      configurations: {},
      anchorEl: null,
      currentConfig: null,
      editableConfig: {},
      currentKey: null,
      newConfig: false,
    };
  }

  componentDidMount(): void {
    this.onConfigurationAdded();
  }

  private handleClick = (e: React.MouseEvent<HTMLElement>) => {
    const configs = getConfigurations();
    this.setState({
      configurations: configs.configurations,
      currentConfig: configs.currentlySelected,
      anchorEl: e.currentTarget,
    });
  };

  private handleSettingsClose = () => {
    const configs = getConfigurations();
    this.setState({
      addNewConfiguration: false,
      currentConfig: configs.currentlySelected,
    });
  };

  private handleClose = () => {
    this.setState({ anchorEl: null });
  };

  private openSettings = (value: boolean) => {
    this.setState({
      addNewConfiguration: true,
      anchorEl: null,
    });
    this.checkForNewConfigClick(value);
  };

  private checkForNewConfigClick = (value: boolean) => {
    this.setState({
      newConfig: value,
    });
  };

  private onConfigurationAdded = () => {
    const configs = getConfigurations();
    this.setState({
      configurations: configs.configurations,
      currentConfig: configs.currentlySelected,
      addNewConfiguration: false,
    })
  };

  private resetConfigs = () => {
    const configs = getConfigurations();
    this.setState({
      configurations: configs.configurations,
      currentConfig: null,
    });
  };

  render() {
    const {
      addNewConfiguration,
      configurations,
      anchorEl,
      currentConfig,
      editableConfig,
      currentKey,
      newConfig,
    } = this.state;

    const {
      modelId,
      treeId,
      treeType,
      quantifyDisabled,
      classes,
    } = this.props;

    return (
      <Fragment>
        <div className={classes.buttonGroup}>
          <QuantifyTreeButton
            modelId={modelId}
            treeId={treeId}
            treeType={treeType}
            disabled={quantifyDisabled || !currentConfig}
          />
          <ActionBarButton
            onClick={this.handleClick}
            color="secondary"
            variant="contained"
            classOverride={classes.downIcon}
          >
            <ArrowDown/>
          </ActionBarButton>
          <ActionBarMenu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={this.handleClose}
            placementOverride="bottom-end"
          >
            <MenuList dense disablePadding className={classes.menu}>
              <MenuItem onClick={() => {this.setState({ currentKey: null}, () => this.openSettings(true))}}>
                <ListItemText primary='+ New Configuration' className={classes.newConfiguration} disableTypography />
              </MenuItem>
              {Object.keys(configurations || {}).map((key: any) => (
                <MenuItem key={key} selected={currentConfig === key} onClick={() => { updateCurrentlySelected(key); this.setState({ currentConfig: key }, () => { this.handleClose() }) }}>
                  <ListItemText primary={key} className={classes.allConfigs} disableTypography />
                  <ActionBarButton
                    onClick={e => {
                      e.stopPropagation();
                      const config = getConfigurations();
                      this.setState({
                        currentKey: key,
                        editableConfig: { [key]: config.configurations[key] },
                      }, () => this.openSettings(false));
                    }}
                    classOverride={classes.editIcon}
                  >
                    <Edit />
                  </ActionBarButton>
                  <ActionBarButton
                    onClick={e => { deleteConfiguration(e, key); this.resetConfigs(); }}
                    classOverride={classes.deleteIcon}
                  >
                    <Close />
                  </ActionBarButton>
                </MenuItem>
              ))}
            </MenuList>
          </ActionBarMenu>
        </div>
        <UserQuantificationSettings
          modelId={modelId}
          treeId={treeId}
          treeType={treeType}
          open={addNewConfiguration}
          onClose={this.handleSettingsClose}
          editableConfig={editableConfig}
          currentKey={currentKey}
          newConfig={newConfig}
        />
      </Fragment>
    );
  }
}

export default withStyles(styles)(Quantification);
